import React, { useMemo, useRef, useState } from 'react';
import { CircuitContents } from './CircuitDrawing';
import { bounds } from '../utils/routing';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 8;

export default function PracticeCircuit({ project }) {
  const frame = useMemo(() => bounds(project), [project]);
  const [view, setView] = useState({ zoom: 1, x: 0, y: 0 });
  const drag = useRef(null);
  const width = frame.w / view.zoom;
  const height = frame.h / view.zoom;
  const x = frame.x + (frame.w - width) / 2 + view.x;
  const y = frame.y + (frame.h - height) / 2 + view.y;
  const zoom = factor => setView(current => ({
    ...current,
    zoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, current.zoom * factor)),
  }));
  const fit = () => setView({ zoom: 1, x: 0, y: 0 });
  const startPan = event => {
    if (event.button !== 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const scale = Math.min(rect.width / width, rect.height / height);
    if (!scale) return;
    drag.current = { id: event.pointerId, clientX: event.clientX, clientY: event.clientY, x: view.x, y: view.y, scale };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.currentTarget.focus();
    event.preventDefault();
  };
  const pan = event => {
    const start = drag.current;
    if (!start || start.id !== event.pointerId) return;
    setView(current => ({ ...current,
      x: start.x - (event.clientX - start.clientX) / start.scale,
      y: start.y - (event.clientY - start.clientY) / start.scale,
    }));
  };
  const endPan = event => {
    if (drag.current?.id !== event.pointerId) return;
    drag.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };
  const keyDown = event => {
    if (event.key === '+' || event.key === '=') zoom(1.25);
    else if (event.key === '-') zoom(0.8);
    else if (event.key === '0') fit();
    else if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      setView(current => ({ ...current,
        x: current.x + (event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0) * width / 10,
        y: current.y + (event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0) * height / 10,
      }));
    } else return;
    event.preventDefault();
  };

  return <section className="practice-reference" aria-label="Practice circuit">
    <div className="practice-reference-heading">
      <h3>Circuit</h3>
      <div className="practice-zoom" role="group" aria-label="Circuit zoom controls">
        <button aria-label="Zoom out circuit" title="Zoom out" disabled={view.zoom <= MIN_ZOOM} onClick={() => zoom(0.8)}>−</button>
        <output aria-label="Circuit zoom level">{Math.round(view.zoom * 100)}%</output>
        <button aria-label="Zoom in circuit" title="Zoom in" disabled={view.zoom >= MAX_ZOOM} onClick={() => zoom(1.25)}>+</button>
        <button onClick={fit} title="Fit the whole circuit">Fit</button>
      </div>
    </div>
    <div className="practice-circuit-viewport">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox={`${x} ${y} ${width} ${height}`} preserveAspectRatio="xMidYMid meet"
        role="img" aria-label="Circuit schematic" tabIndex="0"
        onPointerDown={startPan} onPointerMove={pan} onPointerUp={endPan} onPointerCancel={endPan} onLostPointerCapture={() => { drag.current = null; }} onKeyDown={keyDown}>
        <title>Circuit reference. Use plus or minus to zoom, arrow keys to pan, and 0 to fit.</title>
        <g pointerEvents="none"><CircuitContents project={project}/></g>
      </svg>
    </div>
    <p className="helper practice-pan-hint">Drag to move · + / − to zoom · Fit to reset</p>
  </section>;
}
